import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  faSignOut,
  faUserEdit,
  faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { createBemBlockBuilder, Popover } from '@ebsco-ui/ebsco-ui';

import { FeatureFlags, Icon } from '@app/components';
import { useAppContext } from '@app/contexts';
import { sharedMessages } from '@app/translations';
import { FEATURE, ROUTE } from '@app/constants';

import { AccountIcon } from '../AccountIcon';

import './AccountInfo.scss';

const cnBem = createBemBlockBuilder(['accountInfo']);

export const AccountInfo: FC = () => {
  const { $t } = useIntl();
  const { patronSession, isLoggedIn } = useAppContext();

  const getUserInfo = () => {
    if (!(patronSession && isLoggedIn)) {
      return null;
    }

    return (
      <FeatureFlags
        flagsToMatch={[{ name: FEATURE.consortiaLoginFlow }]}
        renderOn={() => (
          <div className={cnBem('__username')} data-auto="user-name">
            {patronSession.user?.externalSystemId}
          </div>
        )}
        renderOff={() => (
          <>
            <div className={cnBem('__username')} data-auto="user-name">
              {patronSession.user?.firstName} {patronSession.user?.lastName}
            </div>
            <div className={cnBem('__userEmail')} data-auto="user-email">
              {patronSession.user?.email}
            </div>
          </>
        )}
      />
    );
  };

  return (
    <Popover
      uniqueName="account-info"
      panelPlacement="bottom-end"
      className={cnBem('__wrapper')}
    >
      <Popover.ContextConsumer>
        {({ closePopover }) => (
          <>
            <Popover.Button
              styleType="flat-icon"
              id="account-info-button"
              className="popoverButton"
              aria-label={$t({
                id: 'navigation.openUserInfo',
                defaultMessage: 'Open user info',
              })}
            >
              <AccountIcon />
            </Popover.Button>
            <Popover.Panel
              aria-describedby="account-info-button"
              className="popoverWrapper"
            >
              <h2 className={cnBem('__header')}>
                {isLoggedIn ? (
                  <FormattedMessage
                    id="navigation.myAccount"
                    defaultMessage="My account"
                  />
                ) : (
                  <FormattedMessage
                    id="navigation.libraryUser"
                    defaultMessage="Library user"
                  />
                )}
              </h2>
              <div className={cnBem('__userInfoWrapper')}>
                <AccountIcon className={cnBem('__userIcon')} />
                {patronSession && isLoggedIn && (
                  <div className={cnBem('__userInfo')}>{getUserInfo()}</div>
                )}
              </div>
              <ul className={cnBem('__links')}>
                <FeatureFlags
                  flagsToMatch={[{ name: FEATURE.consortiaLoginFlow }]}
                  renderOff={() => (
                    <li>
                      <Icon icon={faUserEdit} className={cnBem('__linkIcon')}>
                        <FormattedMessage {...sharedMessages.preferences}>
                          {message => (
                            <Link to={ROUTE.preferences} onClick={closePopover}>
                              {message[0]}
                            </Link>
                          )}
                        </FormattedMessage>
                      </Icon>
                    </li>
                  )}
                />
                <li>
                  <Icon icon={faSignOut} className={cnBem('__linkIcon')}>
                    {isLoggedIn ? (
                      <FormattedMessage
                        id="navigation.signOut"
                        defaultMessage="Sign out"
                      >
                        {message => <Link to={ROUTE.logout}>{message[0]}</Link>}
                      </FormattedMessage>
                    ) : (
                      <FormattedMessage {...sharedMessages.signIn}>
                        {message => <Link to={ROUTE.login}>{message[0]}</Link>}
                      </FormattedMessage>
                    )}
                  </Icon>
                </li>
                {!isLoggedIn && (
                  <FeatureFlags
                    flagsToMatch={[{ name: FEATURE.preregistration }]}
                  >
                    <li>
                      <Icon icon={faUserPlus} className={cnBem('__linkIcon')}>
                        <FormattedMessage
                          id="navigation.createAccount"
                          defaultMessage="Create account"
                        >
                          {message => (
                            <Link to={ROUTE.createAccount}>{message[0]}</Link>
                          )}
                        </FormattedMessage>
                      </Icon>
                    </li>
                  </FeatureFlags>
                )}
              </ul>
            </Popover.Panel>
          </>
        )}
      </Popover.ContextConsumer>
    </Popover>
  );
};
